"use client";

import { useRef, useState, type ReactNode, type RefObject } from "react";
import { BudgetFlexibilityRows } from "./BudgetFlexibilityRows";
import { OptionPill } from "./OptionPill";
import { RangeSlider } from "./RangeSlider";
import { QuizStepShell } from "./QuizStepShell";
import { STEP_IMAGES, type StepImageEntry } from "./QuizStepImage";
import { Stepper } from "./Stepper";
import {
  CURRENCY_CONFIG,
  SHOW_PREFERRED_DESTINATION,
  SUPPORTED_CURRENCIES,
  type SupportedCurrency,
} from "./quizConstants";
import { formatCurrency } from "@/lib/money";
import { humanizeGeoValue, type GeographyOptions } from "@/lib/api/recommendations/geography";
import { Select, SELECT_NONE_VALUE } from "@/components/ui/Select";

import { missingConstraintFields, type ConstraintsDraft, type TripProfileDraft } from "./quizSchemas";
export type { ConstraintsDraft };

export function QuizStepConstraints({
  value,
  trip,
  onChange,
  onNext,
  onSubmitSkipReview,
  onBack,
  onChangeTrip,
  nextDisabled,
  disabled,
  // Defaults to the real pool in its declared order (not shuffled) — only
  // QuizWizard passes a real, session-shuffled order in production;
  // callers that don't care about images (most component tests) can omit
  // this entirely, matching the old imageIndex?'s ergonomics.
  imageOrder = STEP_IMAGES.constraints,
  imagePriority,
  geoOptions,
  geoError,
  banner,
}: {
  value: ConstraintsDraft;
  trip: TripProfileDraft;
  onChange: (next: ConstraintsDraft) => void;
  onNext: () => void;
  onSubmitSkipReview: () => void;
  onBack?: () => void;
  onChangeTrip: (next: TripProfileDraft) => void;
  nextDisabled?: boolean;
  disabled?: boolean;
  imageOrder?: StepImageEntry[];
  imagePriority?: boolean;
  geoOptions: GeographyOptions | null;
  geoError: boolean;
  banner?: ReactNode;
}) {
  const currencyCfg = CURRENCY_CONFIG[value.currency]!;

  const childrenCount = trip.childrenUnder12;
  const childrenYesSelected = childrenCount !== null && childrenCount >= 1;
  const childrenNoSelected = childrenCount === 0;

  const disabilitiesYes = trip.hasDisabilities === true;
  const disabilitiesNo = trip.hasDisabilities === false;

  // Single source of truth for "what's missing" — shared with
  // useQuizWizardController's canContinue gate so the two can't drift (they
  // did, twice, before this existed).
  const missing = missingConstraintFields(value, trip);

  // Revealed only after a blocked submit attempt is tapped — see
  // handleBlockedNext. Self-clears per field as canContinue's own inputs
  // change, since each message below is gated on `hasAttemptedSubmit && missingX`.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const childrenRef = useRef<HTMLDivElement>(null);
  const disabilitiesRef = useRef<HTMLDivElement>(null);
  // Scoped to just the Mobility/Visual/Hearing pills — OptionPill has no
  // role distinguishing it from the Yes/No pills above it, so a focus
  // target for "which sub-type is missing" needs its own container to
  // search within, not disabilitiesRef's first <button> (that's Yes/No).
  const disabilityTypeRef = useRef<HTMLDivElement>(null);
  const budgetRef = useRef<HTMLDivElement>(null);

  function handleBlockedNext() {
    setHasAttemptedSubmit(true);
    // DOM order matches the step's real layout below (children → solo →
    // disabilities → budget last) so the user lands on the topmost
    // unanswered question, not just "a" missing one.
    const fields: [boolean, RefObject<HTMLDivElement | null>][] = [
      [missing.children, childrenRef],
      [missing.disabilities || missing.disabilityType, disabilitiesRef],
      [missing.budget, budgetRef],
    ];
    const first = fields.find(([isMissing]) => isMissing)?.[1];
    first?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    // The sub-type case needs its own branch: disabilitiesRef's first
    // <button> in DOM order is always the "Yes" (or "No") pill, already
    // pressed — never one of the actually-unanswered sub-type pills. Outside
    // that case, prefer the slider explicitly (a combined selector list
    // resolves in DOM order, not selector order, and the budget block's
    // currency <Select> trigger — also a real <button> — renders before the
    // RangeSlider thumb, so a combined selector would grab the dropdown).
    const target =
      missing.disabilityType && !missing.disabilities
        ? disabilityTypeRef.current?.querySelector<HTMLElement>("button")
        : (first?.current?.querySelector<HTMLElement>('[role="slider"]') ??
          first?.current?.querySelector<HTMLElement>("button"));
    target?.focus({ preventScroll: true });
  }

  function handleCurrencyChange(nextCurrency: SupportedCurrency) {
    // Reset to unset rather than carrying over a value or substituting a
    // default the user never picked — min/max/step differ per currency, so
    // a budget set on one scale isn't a deliberate choice on another.
    onChange({
      ...value,
      currency: nextCurrency,
      budget: null,
    });
  }

  function handleCountryChange(nextCountry: string | null) {
    onChange({ ...value, preferredCountry: nextCountry, preferredRegion: null });
  }

  function handleRegionChange(nextRegion: string | null) {
    onChange({ ...value, preferredRegion: nextRegion });
  }

  const regionOptions =
    geoOptions && value.preferredCountry
      ? (geoOptions.areas_by_country[value.preferredCountry] ?? [])
      : [];

  return (
    <QuizStepShell
      step="Step 02 of 02"
      imageOrder={imageOrder}
      imagePriority={imagePriority}
      title="Tell us about your trip"
      nav={{
        onNext: onSubmitSkipReview,
        nextLabel: "Find my lodges",
        nextDisabled,
        onNextBlocked: disabled ? undefined : handleBlockedNext,
        onBack,
        secondary: {
          label: "Review preferences",
          onClick: onNext,
          disabled: nextDisabled,
          onBlocked: disabled ? undefined : handleBlockedNext,
        },
      }}
      banner={banner}
    >
      {/* Children question. mt-6 is breathing room below the header — only
          meaningful on mobile, where the header sits directly above here in
          flow order. On desktop the header moved above the photo instead
          (see PhotoSplit.tsx), so md:mt-0 removes what would otherwise be
          an orphaned gap above this block. This used to be the budget
          block's own comment — it moved here when budget moved to the end
          of the form (so it's easier for the eye to answer the other
          questions first without missing the disabled CTA reason). */}
      <div className="mt-6 md:mt-0" ref={childrenRef}>
        <p className="text-base font-semibold text-text">
          Are you travelling with children under 12?
        </p>
        <div className="mt-3 flex gap-3">
          <OptionPill
            selected={childrenYesSelected}
            onSelect={() => onChangeTrip({ ...trip, childrenUnder12: childrenCount !== null && childrenCount >= 1 ? childrenCount : 1, soloTraveller: false })}
            disabled={disabled}
          >
            Yes
          </OptionPill>
          <OptionPill
            selected={childrenNoSelected}
            onSelect={() => onChangeTrip({ ...trip, childrenUnder12: 0, soloTraveller: null })}
            disabled={disabled}
          >
            No
          </OptionPill>
        </div>
        {childrenYesSelected && (
          <div className="mt-3">
            <p className="mb-1.5 text-xs font-medium text-text-muted">
              How many children?
            </p>
            <Stepper
              value={childrenCount}
              onChange={(n) => onChangeTrip({ ...trip, childrenUnder12: n })}
              min={1}
              max={10}
              unitSingular="child"
              unitPlural="children"
              decreaseLabel="Decrease children"
              increaseLabel="Increase children"
            />
          </div>
        )}
        {hasAttemptedSubmit && missing.children && (
          <p role="alert" className="mt-3 text-xs font-medium text-alert-red">
            Choose an answer to continue.
          </p>
        )}
      </div>

      {!childrenYesSelected && (
        <div className="mt-6">
          <p className="text-base font-semibold text-text">
            Are you travelling solo?
          </p>
          <div className="mt-3 flex gap-3">
            <OptionPill
              selected={trip.soloTraveller === true}
              onSelect={() => onChangeTrip({ ...trip, soloTraveller: true })}
              disabled={disabled}
            >
              Yes
            </OptionPill>
            <OptionPill
              selected={trip.soloTraveller === false}
              onSelect={() => onChangeTrip({ ...trip, soloTraveller: false })}
              disabled={disabled}
            >
              No
            </OptionPill>
          </div>
        </div>
      )}

      <div className="mt-6" ref={disabilitiesRef}>
        <p className="text-base font-semibold text-text">
          Do any members of your group have disabilities or accessibility needs?
        </p>
        <div className="mt-3 flex gap-3">
          <OptionPill
            selected={disabilitiesYes}
            onSelect={() => onChangeTrip({ ...trip, hasDisabilities: true })}
            disabled={disabled}
          >
            Yes
          </OptionPill>
          <OptionPill
            selected={disabilitiesNo}
            onSelect={() => onChangeTrip({ ...trip, hasDisabilities: false, disabilityMobility: false, disabilityVisual: false, disabilityHearing: false })}
            disabled={disabled}
          >
            No
          </OptionPill>
        </div>
        {disabilitiesYes && (
          <div className="mt-3">
            <p className="mb-3 text-xs font-medium text-text-muted">
              Which apply? (select all that apply)
            </p>
            <div className="flex flex-wrap gap-3" ref={disabilityTypeRef}>
              <OptionPill
                selected={trip.disabilityMobility}
                onSelect={() => onChangeTrip({ ...trip, disabilityMobility: !trip.disabilityMobility })}
                disabled={disabled}
              >
                Mobility
              </OptionPill>
              <OptionPill
                selected={trip.disabilityVisual}
                onSelect={() => onChangeTrip({ ...trip, disabilityVisual: !trip.disabilityVisual })}
                disabled={disabled}
              >
                Visual
              </OptionPill>
              <OptionPill
                selected={trip.disabilityHearing}
                onSelect={() => onChangeTrip({ ...trip, disabilityHearing: !trip.disabilityHearing })}
                disabled={disabled}
              >
                Hearing
              </OptionPill>
            </div>
            {!trip.disabilityMobility &&
              !trip.disabilityVisual &&
              !trip.disabilityHearing && (
              <p role="alert" className="mt-3 text-xs font-medium text-alert-red">
                Select at least one to continue.
              </p>
            )}
          </div>
        )}
        {hasAttemptedSubmit && missing.disabilities && (
          <p role="alert" className="mt-3 text-xs font-medium text-alert-red">
            Choose an answer to continue.
          </p>
        )}
      </div>

      {/* Preferred destination — paused, see SHOW_PREFERRED_DESTINATION */}
      {SHOW_PREFERRED_DESTINATION && geoOptions && geoOptions.countries.length > 0 ? (
        <div className="mt-8" style={{ maxWidth: 520 }}>
          <p className="text-base font-semibold text-text">
            Do you have a preferred destination?
          </p>
          <p className="mt-1 text-xs font-medium text-text-muted">Optional. Leave blank for no preference.</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 min-w-0">
              <Select
                ariaLabel="Preferred country"
                value={value.preferredCountry ?? SELECT_NONE_VALUE}
                onChange={(v) => handleCountryChange(v === SELECT_NONE_VALUE ? null : v)}
                disabled={disabled}
                placeholder="No preference"
                className="w-full justify-between"
                items={[
                  { value: SELECT_NONE_VALUE, label: "No preference" },
                  ...geoOptions.countries.map((o) => ({
                    value: o.value,
                    label: o.label,
                    comingSoon: o.coming_soon,
                  })),
                ]}
              />
            </div>
            {value.preferredCountry && regionOptions.length > 0 && (
              <div className="flex-1 min-w-0">
                <Select
                  ariaLabel="Preferred region"
                  value={value.preferredRegion ?? SELECT_NONE_VALUE}
                  onChange={(v) => handleRegionChange(v === SELECT_NONE_VALUE ? null : v)}
                  disabled={disabled}
                  placeholder="Any region"
                  className="w-full justify-between"
                  items={[
                    { value: SELECT_NONE_VALUE, label: "Any region" },
                    ...regionOptions.map((o) => ({
                      value: o.value,
                      label: o.label,
                      comingSoon: o.coming_soon,
                    })),
                  ]}
                />
              </div>
            )}
          </div>
        </div>
      ) : SHOW_PREFERRED_DESTINATION && geoError ? (
        <div className="mt-8" style={{ maxWidth: 520 }}>
          <p className="text-base font-semibold text-text">
            Do you have a preferred destination?
          </p>
          <p className="mt-1 text-xs font-medium text-text-muted">
            {value.preferredCountry ? (
              <>
                Destination options couldn&apos;t be loaded, but we&apos;ll keep your saved
                preference: {humanizeGeoValue(value.preferredCountry)}
                {value.preferredRegion ? `, ${humanizeGeoValue(value.preferredRegion)}` : ""}.
              </>
            ) : (
              "Destination options couldn't be loaded, so no destination filter will be applied."
            )}
          </p>
        </div>
      ) : null}

      {/* Budget — last: moved off the top of the form so answering the
          other questions first doesn't leave the CTA looking silently
          disabled with the actual blocker scrolled off-screen. Only the
          label/value/slider group below is capped at 520px —
          BudgetFlexibilityRows needs the full form width at md+ (design
          handoff: header-over-photo README §3), so the 520px cap stays on
          its own inner wrapper rather than this outer one. */}
      <div className="mt-8" ref={budgetRef}>
        <div className="max-w-[520px]">
          <p className="text-base font-bold text-text">
            What&apos;s your nightly budget per person?
            {missing.budget && (
              <span className="ml-2 text-xs font-bold uppercase tracking-[0.06em] text-danger">
                Required
              </span>
            )}
          </p>
          <p className="mt-1 text-xs text-text-muted font-medium">
            Adjust the slider to suit your budget
          </p>

          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <div
              className={
                value.budget === null
                  ? "text-text-muted font-semibold text-2xl"
                  : "text-primary font-bold tabular-nums tracking-[-0.01em] text-4xl"
              }
            >
              {value.budget === null ? "Set your budget" : formatCurrency(value.budget, value.currency)}
            </div>
            <Select
              ariaLabel="Currency"
              value={value.currency}
              onChange={handleCurrencyChange}
              disabled={disabled}
              items={SUPPORTED_CURRENCIES.map((c) => ({ value: c, label: c }))}
            />
            <div className="text-xs font-medium text-text-muted">
              per person, per night
            </div>
          </div>

          <div className="mt-5">
            <RangeSlider
              min={currencyCfg.min}
              max={currencyCfg.max}
              step={currencyCfg.step}
              value={value.budget}
              fillColorVar="--bsa-primary"
              disabled={disabled}
              aria-label="Budget"
              showRuler
              unsetValueText="No budget selected"
              invalid={hasAttemptedSubmit && missing.budget}
              onValueChange={(budget) => onChange({ ...value, budget })}
            />
            <div
              className="mt-2.5 flex justify-between tabular-nums text-xs font-medium text-text-muted"
            >
              <span>{formatCurrency(currencyCfg.min, value.currency)}</span>
              <span>{formatCurrency(currencyCfg.max, value.currency)}+</span>
            </div>
          </div>
          {hasAttemptedSubmit && missing.budget && (
            <p role="alert" className="mt-3 text-xs font-medium text-alert-red">
              Set a budget to continue.
            </p>
          )}
        </div>

        <BudgetFlexibilityRows
          value={value.budgetFlexibility}
          onChange={(budgetFlexibility) => onChange({ ...value, budgetFlexibility })}
          disabled={disabled}
        />
      </div>
    </QuizStepShell>
  );
}
